<script lang="ts">
import type { SelectFieldOptions } from '#layers/data-model';
import type { TileRadioGroupProps } from '#layers/ui';
import type { InputBlockProps } from '~/types/input-block';
import { formatTitle } from '@directus/format-title';

interface InputTileRadioBlockProps extends
	SelectFieldOptions,
	InputBlockProps,
	Pick<TileRadioGroupProps, 'size'> {
	/**
	 * Convert the value to title case when no explicit label is provided
	 *
	 * @default true
	 */
	autoTitleCaseValue?: boolean;
}

const __DsBlock = {
	type: 'input',
	name: '$t:blocks.input_tile_radio.name',
	description: '$t:blocks.input_tile_radio.description',
	icon: 'lucide:list-todo',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: { supportedTypes: ({ typeOptions }) => typeOptions?.multiple ? [] : ['select'] },
	options: ({ form, values }) => form([
		{
			name: 'modelValue',
			model: true,
		},
		selectOptionsFormSchema({ options: values.options }),
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'autoTitleCaseValue',
		},
		{
			name: 'readonly',
		},
	]),
}</script>

<script setup lang="ts">
import { reactivePick } from '@vueuse/core';
import { computed } from 'vue';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';
import { selectOptionsFormSchema } from '#layers/data-model';
import { useTranslationUtils } from '#layers/i18n';
import { useDisabledNotice, useReadonly } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';
import { useSelectOptions } from '~/composables/use-select-options';

const props = withDefaults(defineProps<InputTileRadioBlockProps>(), {
	autoTitleCaseValue: true,
});
const model = defineModel<any>();

;

const forwarded = reactivePick(props, 'size');
const { translateString } = useTranslationUtils();
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { normalizedOptions } = useSelectOptions(() => props.options);
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly);

const translatedItems = computed(() => normalizedOptions.value.map((item) => {
	const label = translateString(item.label);
	const description = translateString(item.description);
	return {
		...item,
		label: props.autoTitleCaseValue && label ? formatTitle(label) : label,
		description,
	};
}));
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DTileRadioGroup
			v-bind="forwarded"
			v-model="modelValue"
			:disabled
			:readonly
			:aria-readonly="readonly"
			:items="translatedItems"
			class="grid-cols-1 @w-sm:grid-cols-2"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		/>
	</DTooltip>
</template>
